// Thin wrapper around the ELU Analytics browser SDK (https://elu.dev).
//
// The ELU SDK is injected by an `async` <script> tag in index.html, which
// means `window.elu` does NOT exist synchronously on page load — it only
// appears once that script has finished downloading and executing. These
// helpers centralize the readiness check (and try/catch) so that view code
// never has to touch `window.elu` directly or worry about load timing.

type EluAnalyticsBrowserSdk = {
  identify: (distinctId: string, userProperties?: Record<string, unknown>) => void;
  reset: () => void;
};

// Returns the ELU SDK only once it has fully attached itself to `window`.
// Until the async <script> finishes loading, this returns null.
function getEluAnalyticsBrowserSdkIfLoaded(): EluAnalyticsBrowserSdk | null {
  if (typeof window === "undefined") {
    return null;
  }
  const eluAnalyticsBrowserSdk = (window as any).elu;
  const isEluAnalyticsBrowserSdkLoaded =
    eluAnalyticsBrowserSdk &&
    typeof eluAnalyticsBrowserSdk.identify === "function" &&
    typeof eluAnalyticsBrowserSdk.reset === "function";
  return isEluAnalyticsBrowserSdkLoaded ? (eluAnalyticsBrowserSdk as EluAnalyticsBrowserSdk) : null;
}

// How long to keep retrying `identify` while waiting for the async ELU
// <script> to finish loading, and how often to re-check. The script is
// small and CDN-cached, so it almost always loads within a second or two;
// the generous timeout just covers slow connections.
const MAX_MILLISECONDS_TO_WAIT_FOR_ELU_SDK_TO_LOAD = 15_000;
const MILLISECONDS_BETWEEN_ELU_SDK_LOAD_CHECKS = 250;

// `identify` retry timers that are still waiting for the async ELU SDK to
// load. `resetEluAnalyticsUser` cancels these on sign-out so an identify
// call that was queued for the now-signed-out user can't fire (and re-attach
// their identity) after their reset has already run.
const pendingEluIdentifyRetryTimers = new Set<ReturnType<typeof setInterval>>();

// Identifies the signed-in user to ELU Analytics.
//
// Because the ELU <script> is loaded `async`, the first auth callback on a
// page load (e.g. a returning user refreshing the page) can run BEFORE the
// SDK has loaded. A plain `if (window.elu)` check would silently drop the
// call in that case and the user would never be identified for the session.
// To avoid that, we retry on a short interval until the SDK is available
// (or the timeout is reached).
export function identifyEluAnalyticsUser(
  signedInUserEmail: string,
  userProperties: Record<string, unknown> = { email: signedInUserEmail }
): void {
  const attemptToIdentifyEluUser = (): boolean => {
    const eluAnalyticsBrowserSdk = getEluAnalyticsBrowserSdkIfLoaded();
    if (!eluAnalyticsBrowserSdk) {
      return false;
    }
    try {
      eluAnalyticsBrowserSdk.identify(signedInUserEmail, userProperties);
    } catch (identifyEluAnalyticsError) {
      // Never rethrow: an ELU failure (ad-blocker, network hiccup, API
      // change) must not propagate into the auth flow that calls this.
      console.error("[analytics] failed to identify user to ELU Analytics", identifyEluAnalyticsError);
    }
    return true;
  };

  if (attemptToIdentifyEluUser()) {
    return;
  }

  // SDK not loaded yet — poll until it is (or give up after the timeout).
  let millisecondsWaitedForEluSdkToLoad = 0;
  const eluSdkLoadPollTimer = setInterval(() => {
    millisecondsWaitedForEluSdkToLoad += MILLISECONDS_BETWEEN_ELU_SDK_LOAD_CHECKS;
    const wasEluUserIdentified = attemptToIdentifyEluUser();
    const hasWaitedTooLongForEluSdkToLoad =
      millisecondsWaitedForEluSdkToLoad >= MAX_MILLISECONDS_TO_WAIT_FOR_ELU_SDK_TO_LOAD;
    if (wasEluUserIdentified || hasWaitedTooLongForEluSdkToLoad) {
      clearInterval(eluSdkLoadPollTimer);
      pendingEluIdentifyRetryTimers.delete(eluSdkLoadPollTimer);
    }
  }, MILLISECONDS_BETWEEN_ELU_SDK_LOAD_CHECKS);
  pendingEluIdentifyRetryTimers.add(eluSdkLoadPollTimer);
}

// Drops the previous user's identity from ELU Analytics at sign-out so a
// shared machine doesn't attribute the next user to the previous one.
//
// Unlike `identifyEluAnalyticsUser`, this does NOT retry/defer when the SDK
// isn't loaded yet. A deferred reset could fire AFTER the next user has
// already signed in and been identified, wiping THEIR identity. If the SDK
// isn't loaded by the time someone signs out (extremely unlikely), there is
// no identity to reset anyway, so skipping it is correct.
export function resetEluAnalyticsUser(): void {
  // Cancel any identify retries still waiting for the SDK to load — the user
  // has signed out, so a late identify would wrongly re-attach their identity.
  pendingEluIdentifyRetryTimers.forEach((pendingEluIdentifyRetryTimer) => {
    clearInterval(pendingEluIdentifyRetryTimer);
  });
  pendingEluIdentifyRetryTimers.clear();

  const eluAnalyticsBrowserSdk = getEluAnalyticsBrowserSdkIfLoaded();
  if (!eluAnalyticsBrowserSdk) {
    return;
  }
  try {
    eluAnalyticsBrowserSdk.reset();
  } catch (resetEluAnalyticsError) {
    console.error("[analytics] failed to reset ELU Analytics state", resetEluAnalyticsError);
  }
}
